import * as THREE from 'three';
import { createHomeFieldTexture } from '../assets/canvasTexture';
import { FieldQuilt } from './FieldQuilt';
import {
  TERRAIN_WRAP,
  corridors,
  riverCentre,
  riverWidth,
  roadCentre,
} from './terrainShape';

export class HomeFieldEnvironment {
  readonly group = new THREE.Group();
  private readonly groundTexture = createHomeFieldTexture();
  private readonly groundMaterial = new THREE.MeshStandardMaterial({
    color: '#667045',
    map: this.groundTexture,
    roughness: 0.94,
    metalness: 0,
  });
  private readonly groundGeometry = new THREE.PlaneGeometry(300, 180);
  private readonly riverMaterial = new THREE.MeshPhysicalMaterial({
    // Deep silt green-brown — the James is a tidal river, not a swimming pool.
    color: '#1b3a3f',
    // A mirror-smooth river throws one blown-out specular hotspot from the
    // sun, which the bloom pass then amplifies into a white blob sitting in
    // the same screen position all stage. Broadening the highlight turns that
    // blob into a sheen that travels with the meander, which is what moving
    // water actually does.
    roughness: 0.34,
    metalness: 0.0,
    clearcoat: 0.75,
    clearcoatRoughness: 0.38,
    specularIntensity: 0.45,
    // Low envMap so the sky glints on it rather than washing it out.
    envMapIntensity: 0.5,
  });
  private readonly roadMaterial = new THREE.MeshStandardMaterial({
    color: '#3c3c3e',
    roughness: 0.94,
    metalness: 0,
  });
  /** Centre line. Without it asphalt is just a grey stripe. */
  private readonly roadLineMaterial = new THREE.MeshBasicMaterial({ color: '#c8b45a' });
  /** Pale shoulder either side, so the road has edges. */
  private readonly shoulderMaterial = new THREE.MeshStandardMaterial({
    color: '#7d7566',
    roughness: 0.95,
    metalness: 0,
  });
  private readonly bankMaterial = new THREE.MeshStandardMaterial({
    color: '#5c5442',
    roughness: 0.96,
    metalness: 0,
  });
  /** Every scrolling terrain segment, so update() can wrap them. */
  private readonly riverSegments: THREE.Mesh[] = [];
  /** Crop parcels, hedgerows and farm tracks — see FieldQuilt. */
  private readonly quilt: FieldQuilt;
  private readonly barnGeometry = new THREE.BoxGeometry(1.45, 1.05, 0.42);
  private readonly roofGeometry = new THREE.ConeGeometry(1.12, 0.62, 4);
  private readonly barnMaterial = new THREE.MeshStandardMaterial({
    color: '#8d3029',
    roughness: 0.78,
    metalness: 0.02,
  });
  private readonly roofMaterial = new THREE.MeshStandardMaterial({
    color: '#2f302b',
    roughness: 0.86,
    metalness: 0.06,
  });
  private readonly barns = new THREE.InstancedMesh(
    this.barnGeometry,
    this.barnMaterial,
    18,
  );
  private readonly roofs = new THREE.InstancedMesh(
    this.roofGeometry,
    this.roofMaterial,
    18,
  );
  private readonly barnX = new Float32Array(18);
  private readonly barnY = new Float32Array(18);
  private readonly barnScale = new Float32Array(18);
  private readonly dummy = new THREE.Object3D();
  private readonly echo = new THREE.Group();
  private echoActive = false;
  private echoTime = 0;

  constructor() {
    this.group.name = 'HOME_FIELD';
    const ground = new THREE.Mesh(this.groundGeometry, this.groundMaterial);
    ground.position.z = -2.4;
    ground.receiveShadow = true;
    this.group.add(ground);

    // The James, built as a meander. Deterministic offsets so the stage is
    // identical every run.
    // The James. One continuous ribbon, meander baked into the centreline.
    //
    // Centrelines now live in terrainShape.ts so the prop layout can be
    // validated against the same curves the water is actually drawn from.
    // Step counts scale with the wrap. At 900 units, 128 steps would put a
    // vertex every 7 units and turn the meander into a polygon.
    const RIVER_STEPS = 448;
    const ROAD_STEPS = 336;

    const river = new THREE.Mesh(
      ribbonGeometry(riverCentre, riverWidth, -TERRAIN_WRAP / 2, TERRAIN_WRAP / 2, RIVER_STEPS),
      this.riverMaterial,
    );
    river.position.z = -2.15;
    river.receiveShadow = true;
    this.riverSegments.push(river);
    this.group.add(river);

    // Banks are their own ribbons offset from the same centreline, so they
    // track the water exactly instead of approximating it.
    for (const side of [-1, 1]) {
      const bank = new THREE.Mesh(
        ribbonGeometry(
          (y) => riverCentre(y) + side * (riverWidth(y) / 2 + 0.55),
          () => 1.2,
          -TERRAIN_WRAP / 2, TERRAIN_WRAP / 2, RIVER_STEPS,
        ),
        this.bankMaterial,
      );
      bank.position.z = -2.13;
      this.riverSegments.push(bank);
      this.group.add(bank);
    }

    // County road, also segmented so it bends. Shoulder under, asphalt over,
    // centre line on top — three planes make a strip read as a road.
    const shoulder = new THREE.Mesh(
      ribbonGeometry(roadCentre, () => 4.6, -TERRAIN_WRAP / 2, TERRAIN_WRAP / 2, ROAD_STEPS),
      this.shoulderMaterial,
    );
    shoulder.position.z = -2.12;
    this.riverSegments.push(shoulder);
    this.group.add(shoulder);

    const road = new THREE.Mesh(
      ribbonGeometry(roadCentre, () => 3.4, -TERRAIN_WRAP / 2, TERRAIN_WRAP / 2, ROAD_STEPS),
      this.roadMaterial,
    );
    road.position.z = -2.08;
    this.riverSegments.push(road);
    this.group.add(road);

    // Dashed centre line, placed along the same centreline so it never drifts
    // off the asphalt on a curve.
    // INSTANCED, not one mesh per dash. Dash spacing is fixed at ~5 u, so a
    // 900 u wrap needs 180 of them — as individual meshes that would be 180
    // draw calls for a painted line. One instanced mesh is one call, and it
    // scrolls as a unit like every other terrain segment.
    const DASH_SPACING = 5;
    const DASH_COUNT = Math.round(TERRAIN_WRAP / DASH_SPACING);
    const dashes = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(0.2, 2.8),
      this.roadLineMaterial,
      DASH_COUNT,
    );
    dashes.frustumCulled = false;
    for (let d = 0; d < DASH_COUNT; d += 1) {
      // Spacing divides the wrap exactly so the dash rhythm also loops cleanly.
      const y = -TERRAIN_WRAP / 2 + (d * TERRAIN_WRAP) / DASH_COUNT;
      this.dummy.position.set(roadCentre(y), y, 0);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(1);
      this.dummy.updateMatrix();
      dashes.setMatrixAt(d, this.dummy.matrix);
    }
    dashes.instanceMatrix.needsUpdate = true;
    dashes.position.z = -2.05;
    this.riverSegments.push(dashes);
    this.group.add(dashes);

    // THE QUILT. Built last because it needs both centrelines: parcels are
    // clipped against the river and road corridors so a field never paves
    // over the water. Generous margins — a crop growing to the waterline
    // reads as a texture error, not as farming.
    this.quilt = new FieldQuilt(TERRAIN_WRAP, corridors);
    for (const seg of this.quilt.segments) this.riverSegments.push(seg);
    this.group.add(this.quilt.group);

    this.barns.castShadow = true;
    this.roofs.castShadow = true;
    for (let index = 0; index < this.barnY.length; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      this.barnX[index] = side * (9.8 + (index % 5) * 1.25);
      this.barnY[index] = -31 + index * 3.8;
      this.barnScale[index] = 0.68 + (index % 4) * 0.11;
    }
    // Placeholder barn boxes retired — PropField now carries the dressing.
    this.barns.visible = false;
    this.roofs.visible = false;
    this.group.add(this.barns, this.roofs);
    this.createEcho();
    this.group.add(this.echo);
    this.syncBarns();
  }

  update(delta: number, scrollSpeed: number): void {
    this.groundTexture.offset.y -= (scrollSpeed * delta) / 72;
    // Wrap terrain segments through a 168 u cycle (6 segments x 28 u stride).
    for (const seg of this.riverSegments) {
      seg.position.y -= scrollSpeed * delta;
      if (seg.position.y < -TERRAIN_WRAP / 2 - 6) seg.position.y += TERRAIN_WRAP;
    }
    for (let index = 0; index < this.barnY.length; index += 1) {
      this.barnY[index] -= scrollSpeed * delta;
      if (this.barnY[index] < -31) this.barnY[index] += 68.4;
    }
    this.syncBarns();

    if (this.echoActive) {
      this.echoTime += delta;
      const progress = Math.min(1, this.echoTime / 6);
      this.echo.position.x = -23 + progress * 46;
      this.echo.position.y = 8 + progress * 2.2;
      this.echo.visible = progress < 1;
      if (progress >= 1) this.echoActive = false;
    }
  }

  triggerEcho(): void {
    if (this.echoActive || this.echo.visible) return;
    this.echoActive = true;
    this.echoTime = 0;
    this.echo.visible = true;
  }

  reset(): void {
    this.echoActive = false;
    this.echoTime = 0;
    this.echo.visible = false;
  }

  dispose(): void {
    this.groundTexture.dispose();
    this.groundGeometry.dispose();
    // Segment geometries are created per-segment, so dispose them individually.
    for (const seg of this.riverSegments) seg.geometry.dispose();
    this.barnGeometry.dispose();
    this.roofGeometry.dispose();
    this.groundMaterial.dispose();
    this.quilt.dispose();
    this.riverMaterial.dispose();
    this.roadMaterial.dispose();
    this.barnMaterial.dispose();
    this.roofMaterial.dispose();
    this.echo.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.geometry.dispose();
      if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose());
      else child.material.dispose();
    });
  }

  private syncBarns(): void {
    for (let index = 0; index < this.barnY.length; index += 1) {
      const scale = this.barnScale[index];
      this.dummy.position.set(this.barnX[index], this.barnY[index], -1.7);
      this.dummy.rotation.set(0, 0, index % 3 === 0 ? 0.12 : -0.08);
      this.dummy.scale.setScalar(scale);
      this.dummy.updateMatrix();
      this.barns.setMatrixAt(index, this.dummy.matrix);

      this.dummy.position.z = -1.35;
      this.dummy.rotation.z += Math.PI / 4;
      this.dummy.scale.set(scale, scale, scale * 0.8);
      this.dummy.updateMatrix();
      this.roofs.setMatrixAt(index, this.dummy.matrix);
    }
    this.barns.instanceMatrix.needsUpdate = true;
    this.roofs.instanceMatrix.needsUpdate = true;
  }

  private createEcho(): void {
    this.echo.name = 'AIR_07_ECHO';
    const material = new THREE.MeshStandardMaterial({
      color: '#751f23',
      roughness: 0.7,
      metalness: 0.18,
      emissive: '#2a0c10',
      emissiveIntensity: 0.6,
    });
    const body = new THREE.Mesh(new THREE.ConeGeometry(0.52, 2.8, 4), material);
    body.rotation.z = Math.PI;
    body.scale.set(0.65, 1, 0.24);
    this.echo.add(body);

    const wingMaterial = material.clone();
    const rightWing = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.18, 0.2), wingMaterial);
    rightWing.position.set(0.65, -0.22, 0);
    rightWing.rotation.z = -0.3;
    this.echo.add(rightWing);

    const portStub = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.18, 0.2), wingMaterial);
    portStub.position.set(-0.42, -0.14, 0);
    portStub.rotation.z = 0.2;
    this.echo.add(portStub);
    this.echo.scale.setScalar(0.78);
    this.echo.position.z = -0.7;
    this.echo.visible = false;
  }
}

/**
 * Build a continuous ribbon from a centreline function.
 *
 * Two quads per step share their edge vertices exactly, so the result has no
 * seams however sharply the centreline curves — which is the whole reason
 * stacked axis-aligned planes were replaced.
 *
 * @param centre  x position of the ribbon centre at parameter t
 * @param width   ribbon width at parameter t
 */
function ribbonGeometry(
  centre: (y: number) => number,
  width: (y: number) => number,
  yStart: number,
  yEnd: number,
  steps: number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const span = yEnd - yStart;

  for (let i = 0; i <= steps; i += 1) {
    const y = yStart + (span * i) / steps;
    const c = centre(y);
    const half = width(y) / 2;
    positions.push(c - half, y, 0, c + half, y, 0);
    if (i < steps) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}
